import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { RIBBON_URL } from "@/lib/assets";
import { ArrowRight, Sparkles } from "lucide-react";

export function FiveThingsList() {
  const [eps, setEps] = useState([]);
  useEffect(() => { api.get("/five-things").then(r => setEps(r.data)); }, []);
  return (
    <div className="bg-cream">
      <div className="brand-container py-20">
        <div className="brand-eyebrow">The Series</div>
        <h1 className="brand-h1 mt-3 mb-4">Five takeaways you<br/>should know.</h1>
        <p className="brand-lede max-w-2xl">Bite-sized wisdom from the bench and the front porch — five takeaways at a time.</p>
        <div className="mt-16 space-y-16">
          {eps.map((e, idx) => (
            <div key={e.id} className="grid lg:grid-cols-12 gap-10 items-center">
              <div className={`lg:col-span-6 ${idx % 2 ? "lg:order-2" : ""}`}>
                <img src={e.cover_image || RIBBON_URL} alt="" className="w-full aspect-[4/3] object-cover rounded-lg border border-gold-light" />
              </div>
              <div className={`lg:col-span-6 ${idx % 2 ? "lg:order-1" : ""}`}>
                <div className="brand-eyebrow"><Sparkles className="inline w-4 h-4 mr-1" /> Episode · {e.topic}</div>
                <h2 className="font-serif text-3xl md:text-4xl leading-tight text-charcoal mt-3">{e.title}</h2>
                <p className="mt-3 text-charcoal/70">{e.short_description}</p>
                <ol className="mt-6 space-y-2">
                  {(e.takeaways || []).slice(0,5).map((t, i) => (
                    <li key={i} className="flex gap-3 text-charcoal/85">
                      <span className="font-serif text-gold text-lg">{String(i+1).padStart(2,"0")}</span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ol>
                <Link to={`/five-things/${e.id}`} className="btn-primary mt-8 inline-flex" data-testid={`five-ep-${e.id}`}>
                  Open episode <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
          {eps.length === 0 && <div className="text-charcoal/50">No episodes yet — come back soon.</div>}
        </div>
      </div>
    </div>
  );
}

export function FiveThingsEpisode() {
  const { id } = useParams();
  const [e, setE] = useState(null);
  useEffect(() => { api.get(`/five-things/${id}`).then(r => setE(r.data)).catch(() => setE(false)); }, [id]);
  if (e === false) return <div className="brand-container py-20 text-charcoal/60">Episode not found.</div>;
  if (!e) return <div className="brand-container py-20 text-charcoal/40">Loading…</div>;
  return (
    <div className="bg-cream">
      <div className="max-w-5xl mx-auto px-6 py-16">
        <Link to="/five-things" className="text-sm text-plum hover:underline" data-testid="five-back-link">← Back to series</Link>
        <div className="brand-eyebrow mt-8">Five Takeaways · {e.topic}</div>
        <h1 className="font-serif text-4xl md:text-5xl text-charcoal leading-tight mt-3">{e.title}</h1>
        <p className="mt-4 text-charcoal/70 text-lg">{e.short_description}</p>
        {e.cover_image && (
          <div className="mt-8 rounded-lg overflow-hidden border border-gold-light">
            <img src={e.cover_image} alt="" className="w-full h-auto max-h-[520px] object-cover" />
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-4 mt-12">
          {(e.takeaways || []).map((t, i) => (
            <div key={i} className="editorial-card p-6 flex gap-4">
              <div className="font-serif text-4xl text-gold leading-none">{String(i+1).padStart(2,"0")}</div>
              <div className="text-charcoal/85 leading-relaxed">{t}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
